import { useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  EmptyState,
  HStack,
  Image,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { MdAddPhotoAlternate, MdImage, MdPhotoLibrary } from "react-icons/md";
import { storage } from "lib/firebase";
import { useAddDashboardActividadPhoto } from "features/dashboard/actividades/hooks";
import { useDashboardFotos, useSetDashboardPeriodoCover } from "features/dashboard/fotos/hooks";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const DEFAULT_COVER = "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=60";

function getFileExtension(file) {
  const base = (file?.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  const cleaned = base.replace(/[^a-z0-9]/g, "");
  if (cleaned) return cleaned;
  if (file?.type === "image/jpeg") return "jpg";
  if (file?.type === "image/png") return "png";
  if (file?.type === "image/webp") return "webp";
  return "jpg";
}

function validateImageFile(file) {
  if (!file?.type?.startsWith("image/")) throw new Error("Solo se permiten archivos de imagen.");
  if (file.size > MAX_IMAGE_SIZE_BYTES) throw new Error("El archivo supera el limite de 10MB.");
}

async function uploadImageFile(file, storagePath) {
  validateImageFile(file);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
  const downloadUrl = await getDownloadURL(storageRef);
  return {
    storagePath,
    downloadUrl,
    contentType: file.type || null,
    sizeBytes: file.size,
  };
}

const Fotos = () => {
  const q = useDashboardFotos();
  const setCover = useSetDashboardPeriodoCover();
  const addPhoto = useAddDashboardActividadPhoto();

  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [selectedActividadId, setSelectedActividadId] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [error, setError] = useState("");

  const periodo = q.data?.periodo || null;
  const activities = Array.isArray(q.data?.activities) ? q.data.activities : [];
  const photos = Array.isArray(q.data?.photos) ? q.data.photos : [];
  const actividadId = selectedActividadId || activities[0]?.actividadId || "";
  const coverUrl = periodo?.coverUrl || DEFAULT_COVER;

  async function onCoverFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !periodo?.periodoId) return;

    setError("");
    setUploadingCover(true);
    try {
      const imageId = crypto.randomUUID();
      const ext = getFileExtension(file);
      const uploaded = await uploadImageFile(file, `periodos/${periodo.periodoId}/cover/${imageId}.${ext}`);
      await setCover.mutateAsync({ imageId, ...uploaded });
    } catch (e) {
      setError(String(e?.message || e || "No se pudo subir la portada."));
    } finally {
      setUploadingCover(false);
    }
  }

  async function onGalleryFilesChange(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !actividadId) return;

    setError("");
    setUploadingGallery(true);
    try {
      for (const file of files) {
        const imageId = crypto.randomUUID();
        const ext = getFileExtension(file);
        const uploaded = await uploadImageFile(file, `actividades/${actividadId}/photos/${imageId}.${ext}`);
        await addPhoto.mutateAsync({ actividadId, payload: { imageId, ...uploaded } });
      }
      await q.refetch();
    } catch (e) {
      setError(String(e?.message || e || "No se pudieron subir las fotos."));
    } finally {
      setUploadingGallery(false);
    }
  }

  return (
    <Box>
      <HStack justify="space-between" align={{ base: "start", md: "center" }} mb="6" gap="4" flexWrap="wrap">
        <Box>
          <Text fontFamily="display" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color="dashboard.fg.default" lineHeight="1.1">
            Fotos
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Portada del periodo y galeria de actividades del periodo actual
          </Text>
        </Box>
        <Badge colorPalette="red" variant="subtle" rounded="full" px="3">
          {periodo?.nivelNombre || "Nivel actual"}
        </Badge>
      </HStack>

      {q.isLoading ? (
        <Box py="12" display="flex" justifyContent="center">
          <Spinner />
        </Box>
      ) : q.isError ? (
        <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
          {String(q.error?.message || q.error)}
        </Text>
      ) : (
        <VStack align="stretch" gap="5">
          <Input ref={coverInputRef} type="file" accept="image/*" onChange={onCoverFileChange} display="none" />
          <Input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={onGalleryFilesChange} display="none" />

          <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card" overflow="hidden">
            <Box position="relative" h={{ base: "220px", md: "320px" }} bg="dashboard.bg.surfaceMuted">
              <Image src={coverUrl} alt="Portada del periodo" w="full" h="full" objectFit="cover" />
              <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(0,0,0,0.55), transparent 60%)" />
              <Box position="absolute" left="5" bottom="5" color="white">
                <Text fontFamily="display" fontSize={{ base: "2xl", md: "4xl" }} fontWeight="700" lineHeight="1">
                  {periodo?.nivelNombre || "Nivel"}
                </Text>
                <Text fontSize="sm" opacity="0.86">
                  Esta imagen aparece en el encabezado publico de este nivel durante el periodo actual.
                </Text>
              </Box>
              <Button
                position="absolute"
                top="4"
                right="4"
                colorPalette="red"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
              >
                {uploadingCover ? <Spinner size="sm" /> : <MdImage />}
                Cambiar portada
              </Button>
            </Box>
          </Card.Root>

          <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
            <Card.Header>
              <HStack justify="space-between" align={{ base: "stretch", md: "center" }} gap="3" flexWrap="wrap">
                <Box>
                  <Text fontFamily="display" fontSize="xl" fontWeight="700" color="dashboard.fg.default">
                    Galeria del periodo
                  </Text>
                  <Text fontSize="sm" color="dashboard.fg.muted">
                    Fotos subidas en actividades de este nivel durante el periodo actual.
                  </Text>
                </Box>
                <HStack gap="2" flexWrap="wrap">
                  <NativeSelect.Root w={{ base: "full", md: "260px" }}>
                    <NativeSelect.Field value={actividadId} onChange={(e) => setSelectedActividadId(e.target.value)}>
                      {activities.map((activity) => (
                        <option key={activity.actividadId} value={activity.actividadId}>
                          {activity.nombre}
                        </option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                  <Button
                    colorPalette="red"
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={uploadingGallery || !actividadId}
                  >
                    {uploadingGallery ? <Spinner size="sm" /> : <MdAddPhotoAlternate />}
                    Subir fotos
                  </Button>
                </HStack>
              </HStack>
            </Card.Header>
            <Card.Body>
              {!photos.length ? (
                <EmptyState.Root>
                  <EmptyState.Content>
                    <EmptyState.Indicator>
                      <MdPhotoLibrary />
                    </EmptyState.Indicator>
                    <EmptyState.Title>Sin fotos en este periodo</EmptyState.Title>
                    <EmptyState.Description>
                      Selecciona una actividad y sube fotos para que aparezcan en la galeria publica de este periodo.
                    </EmptyState.Description>
                  </EmptyState.Content>
                </EmptyState.Root>
              ) : (
                <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} gap="3">
                  {photos.map((photo) => (
                    <Box key={photo.imageId} position="relative" overflow="hidden" rounded="card" bg="dashboard.bg.surfaceMuted">
                      <Image src={photo.downloadUrl} alt={photo.actividadNombre || "Foto"} h={{ base: "140px", md: "180px" }} w="full" objectFit="cover" />
                      <Box position="absolute" insetX="0" bottom="0" bg="linear-gradient(to top, rgba(0,0,0,0.68), transparent)" color="white" p="3">
                        <Text fontSize="xs" fontWeight="700" lineClamp={1}>
                          {photo.actividadNombre || "Actividad"}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Card.Body>
          </Card.Root>

          {error ? (
            <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
              {error}
            </Text>
          ) : null}
        </VStack>
      )}
    </Box>
  );
};

export default Fotos;
